import { addressSlice } from '@qbe/dfc-services';
import { Action, combineReducers, configureStore, ThunkAction } from '@reduxjs/toolkit';
import { TargetEnv, targetEnv } from 'config';
import { config, SOURCE_SYSTEM } from 'config';
import { addressApi } from 'services/address';
import { historyQuestionApi } from 'services/history';
import { premiumsApi } from 'services/premiums';
import { quotesApi } from 'services/quotes';
import { refDataApi } from 'services/refData';
import {
  abnReducer,
  businessDetailsReducer,
  coverDetailsReducer,
  coverInclusionsReducer,
  declineReducer,
  extraInfoReducer,
  paymentsReducer,
  quoteReducer,
  reviewAndPayReducer,
  selectCoverReducer,
  stepReducer,
  trackAnalyticsReducer,
} from 'slices';

import { errorHandlerMiddleware } from './middleware/errorHandlerMiddleware';
import { listenerMiddleware } from './middleware/listenerMiddleware';
import { abnApi } from './services/abn';
import { pciPalApi } from './services/pciPal';
import { policyApi } from './services/policy';
import { appDetailsSlice } from './slices/appDetails';
import { payMyBillSlice } from './slices/pay-my-bill/slice';
import analytics from './utils/analytics/analyticsMiddleware';

export const addressApiSlice = addressSlice({
  baseUrl: config.addressApiUrl,
  headers: {
    'X-SourceSystem': SOURCE_SYSTEM,
  },
  debounceAnalyticsMilliseconds: 1000,
});

// Address Slice Hooks
export const {
  useGetAddressesQuery,
  useLazyGetAddressesQuery,
  useStructuredAddressLookupMutation,
  useRiskRatingLookupMutation,
} = addressApiSlice;

const rootReducer = combineReducers({
  [refDataApi.reducerPath]: refDataApi.reducer,
  [addressApi.reducerPath]: addressApi.reducer,
  [addressApiSlice.reducerPath]: addressApiSlice.reducer,
  [premiumsApi.reducerPath]: premiumsApi.reducer,
  [historyQuestionApi.reducerPath]: historyQuestionApi.reducer,
  [quotesApi.reducerPath]: quotesApi.reducer,
  [abnApi.reducerPath]: abnApi.reducer,
  [policyApi.reducerPath]: policyApi.reducer,
  [pciPalApi.reducerPath]: pciPalApi.reducer,
  step: stepReducer,
  businessDetails: businessDetailsReducer,
  selectCover: selectCoverReducer,
  coverDetails: coverDetailsReducer,
  quote: quoteReducer,
  analytics: trackAnalyticsReducer,
  extraInfo: extraInfoReducer,
  reviewAndPay: reviewAndPayReducer,
  payments: paymentsReducer,
  decline: declineReducer,
  abn: abnReducer,
  payMyBill: payMyBillSlice.reducer,
  appDetails: appDetailsSlice.reducer,
  coverInclusions: coverInclusionsReducer,
});

export const setupStore = (preloadedState?: Partial<RootState>) =>
  configureStore({
    reducer: rootReducer,
    preloadedState,
    /*
     * Adding the api middleware enables caching, invalidation, polling,
     * and other useful features of `rtk-query`.
     */
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware().concat(
        listenerMiddleware.middleware,
        refDataApi.middleware,
        addressApi.middleware,
        addressApiSlice.middleware,
        premiumsApi.middleware,
        historyQuestionApi.middleware,
        quotesApi.middleware,
        abnApi.middleware,
        policyApi.middleware,
        pciPalApi.middleware,
        errorHandlerMiddleware,
        analytics
      ),
    devTools: targetEnv !== TargetEnv.prod,
  });

export const store = setupStore(window.digiSmeInitState);

export type AppStore = ReturnType<typeof setupStore>;
export type AppDispatch = AppStore['dispatch'];
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;

export type AppThunkConfig = {
  store: AppStore;
  dispatch: AppDispatch;
};

export type RootState = ReturnType<typeof rootReducer>;
